import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  type SkippedDependency,
} from './types';
import type { AuditDependency } from '../schemas/index';

/**
 * Platform tokens that may appear as a dash-separated suffix after the version
 * number in Gemfile.lock spec lines, e.g. `nokogiri (1.15.4-x86_64-linux)`.
 *
 * These are stripped so only the bare version is reported; the original
 * parenthesised string is preserved as `requirement`.
 *
 * Combined tokens like `x86_64-darwin-19` are handled by the regex greedily
 * consuming all trailing `-<token>` segments.
 */
const PLATFORM_SUFFIX_RE =
  /^(\d+\.\d+(?:\.\d+)*)-(x86_64|x86|aarch64|arm64|arm|universal|java|mswin64|mswin|mingw32|mingw|darwin|linux)([-\w.]*)$/;

/**
 * Strips a platform suffix from a raw version string.
 * Returns `{ version, hasPlatform }` — the caller records `rawVersion` as
 * `requirement` when `hasPlatform` is true.
 */
function stripPlatform(rawVersion: string): { version: string; hasPlatform: boolean } {
  const m = PLATFORM_SUFFIX_RE.exec(rawVersion);
  if (m && m[1] != null) {
    return { version: m[1], hasPlatform: true };
  }
  return { version: rawVersion, hasPlatform: false };
}

/**
 * Section types recognised in a Gemfile.lock.
 * Only GEM, GIT, and PATH sections contain `specs:` blocks we care about.
 */
type Section = 'GEM' | 'GIT' | 'PATH' | 'OTHER';

/**
 * Parses a `Gemfile.lock` manifest (Bundler lockfile) for RubyGems auditing.
 *
 * v1 policy:
 * - Resolved specs from `GEM` sections → exact-version dependencies.
 * - Resolved specs from `GIT` sections → skipped ('sourced from git').
 * - Resolved specs from `PATH` sections → skipped ('sourced from local path').
 * - `PLATFORMS`, `DEPENDENCIES`, `BUNDLED WITH`, `RUBY VERSION` sections are
 *   ignored entirely (DEPENDENCIES lists requested ranges, not resolved versions).
 * - Platform-suffixed versions (`1.15.4-x86_64-linux`) are stripped to the
 *   numeric part; the raw form is stored in `requirement`.
 * - Ruby prereleases use dots (`7.1.0.beta1`) and pass through untouched.
 *
 * Throws `ManifestParseError` when the input contains no GEM/GIT/PATH section
 * header and no `specs:` line — i.e. is clearly not a Gemfile.lock.
 */
export const parseGemfileLock: ManifestParser = (content: string): ParsedManifest => {
  const lines = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  // Sanity check: must have at least one GEM/GIT/PATH header or a specs: line.
  const hasRecognisedStructure = lines.some(
    (l) => /^(GEM|GIT|PATH)$/.test(l.trim()) || l.trimEnd() === '  specs:',
  );
  if (!hasRecognisedStructure) {
    throw new ManifestParseError(
      'Not a valid Gemfile.lock: no GEM/GIT/PATH section or specs: line found',
    );
  }

  const dependencies: AuditDependency[] = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  let currentSection: Section = 'OTHER';
  let inSpecs = false;

  for (const line of lines) {
    const trimmed = line.trimEnd();

    // ---- Detect top-level section headers (no leading spaces) ---------------
    if (/^GEM$/.test(trimmed)) {
      currentSection = 'GEM';
      inSpecs = false;
      continue;
    }
    if (/^GIT$/.test(trimmed)) {
      currentSection = 'GIT';
      inSpecs = false;
      continue;
    }
    if (/^PATH$/.test(trimmed)) {
      currentSection = 'PATH';
      inSpecs = false;
      continue;
    }
    // Any other capitalised header (PLATFORMS, DEPENDENCIES, BUNDLED WITH, etc.)
    if (/^[A-Z]/.test(trimmed)) {
      currentSection = 'OTHER';
      inSpecs = false;
      continue;
    }

    // ---- Blank lines do not reset specs mode (specs: may span many gems) ----

    // ---- Detect `  specs:` (exactly 2-space indent) -------------------------
    if (trimmed === '  specs:' || line === '  specs:') {
      if (currentSection === 'GEM' || currentSection === 'GIT' || currentSection === 'PATH') {
        inSpecs = true;
      }
      continue;
    }

    // ---- Parse spec lines only when inside a relevant specs: block ----------
    if (!inSpecs) continue;
    if (currentSection === 'OTHER') continue;

    // 4-space indent spec line: `    name (version)`
    // 6-space indent lines are gem requirements (ranges inside the spec) — ignored.
    const specLineMatch = /^    ([A-Za-z0-9_.+-]+) \(([^)]+)\)$/.exec(line);
    if (!specLineMatch || specLineMatch[1] == null || specLineMatch[2] == null) continue;

    const name: string = specLineMatch[1];
    const rawVersion: string = specLineMatch[2];

    if (seen.has(name)) continue;
    seen.add(name);

    if (currentSection === 'GIT') {
      skipped.push({ name, requirement: rawVersion, reason: 'sourced from git' });
      continue;
    }
    if (currentSection === 'PATH') {
      skipped.push({ name, requirement: rawVersion, reason: 'sourced from local path' });
      continue;
    }

    // GEM section — strip platform suffix if present.
    const { version, hasPlatform } = stripPlatform(rawVersion);
    const requirement = hasPlatform ? rawVersion : null;
    dependencies.push(dep('RUBYGEMS', name, version, requirement));
  }

  return { system: 'RUBYGEMS', dependencies, skipped };
};
